import { take } from "rxjs/operators";
import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnInit } from "@angular/core";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { UserService } from "@main-services/user.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { ErrorClass, Fee, Fiat, FiatWallet, Offer, PaymentOption, Price, ValidationError } from "@models";
import { SavingsPlan } from "@models/savings_plan";
import { SavingsPlanExample } from "@models/savings_plan_example";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { BehaviorSubject, Observable } from "rxjs";
import { BpSelectorFrequencyModel } from "../../../core/components/bp-selector-frequency/selector-models/bp-selector-frequency-model";
import { PaymentAccount } from "@models/payment_account";
import { Helper, parseList } from "@helpers/functions";
import { ModalHeader } from "@models/modal_step";
import { TradeOfferService } from "../../services/trade-offer.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StockTestService } from "@main-services/stock-test.service";
import { SELECTED_CURRENCY, TRADE_TYPE } from "@models/trade-offer.model";
import { empty } from "@helpers/utils";
import {
	APPROPRIATENESS_REQUIRED_ERROR_CODE,
	ONE_HOUR_EXPIRY,
	TRADABLES_TYPE,
	VERIFICATION_MODAL_ROUTE
} from "@helpers/constants";
import { Router } from "@angular/router";
import { SavingsModel } from "@models/savings";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AccountService } from "../../../account/services/account.service";
import { SavingsSuccessComponent } from "../../../main/components/step-summary/success-screens/savings-success/savings-success.component";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { DepositPendingComponent } from "@modules/main/components/step-summary/success-screens/deposit-pending/deposit-pending.component";
import { TradeComponent } from "../trade.interface";
import { toEuroByFiatId } from "@helpers/tradable-convert";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { SavingsService } from "@main-services/savings.service";
import { trigger } from "@angular/animations";
import { openingClosing } from "../../../../animations/animations";
import { MatDialog } from "@angular/material/dialog";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { VerificationModalFacade } from "@modules/verification/modal/state/verification-modal.facade";
import { ICashPlusFiatWallet } from "@modules/cashplus/entities/cashplus.model";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import {
	SAVINGS_ELIGIBLE_VISION_BENEFIT_FEE_COOKIE,
	SAVINGS_VISION_BENEFIT_FEE_COOKIE
} from "@modules/trade/constants/constants";

@Component({
	templateUrl: "./trade-savings-modal.component.html",
	styleUrls: ["./trade-savings-modal.component.scss", "../trade-modal.component.scss"],
	animations: [trigger("openingClosingTrigger", openingClosing)],
	standalone: false
})
export class TradeSavingsComponent extends TradeComponent implements OnInit, AfterViewInit {
	private readonly stakingFacade = inject(StakingFacade);
	private readonly cdr = inject(ChangeDetectorRef);
	public isLoaded: boolean = false;
	protected label: string = "recurring-buy";
	protected apiCallOption: ApicallOption = new ApicallOption();

	public savingsModel: SavingsModel = new SavingsModel();
	public showSavingsIntro: boolean = !this.savingService.getUserHasSavingsPlan();

	private savingsMigrateToFiatIdEnabled = false;
	private savingsMigrateToFiatIdEnabled$ = this.launchDarklyService.isActive$(LD_FLAG.MIGRATE_SAVINGS_TO_FIAT_ID);

	// Fiat currency selection and options list
	public selectedFiat: Fiat;
	public fiatCurrencyOptions: BpSelectOption[];

	private wantsSepaProvider: boolean = false;

	public showModalContent: boolean = false;

	// Fiat provider list (eg: VISA)
	public providerAvailable: PaymentOption[] = [];
	public providerUnavailable: PaymentOption[] = [];
	public fiats: Fiat[] = [];
	public showSmsPinVerification: boolean = false;
	public showMandateConfirmation: boolean = false;

	private showSavingsIntroModal: boolean = false;
	private modalCanForceDismiss: boolean = false;
	private needsValidationShortcut: boolean = false;
	public selectedSepaAccount: PaymentAccount = null;
	private _sepaAccounts: BehaviorSubject<PaymentAccount[]> = new BehaviorSubject<PaymentAccount[]>([]);
	private frequencyStandard: string = "";

	protected readonly flowName: string = "Savings";

	@Input() public savingsData: SavingsModel = null;
	@Input() public pinSent: boolean = true;
	@Input() public walletId: string = null;

	public wallets: TradableWallet[] = [];

	public constructor(
		protected apicallService: ApicallService,
		protected fiatwalletService: FiatWalletService,
		protected infoService: InfoService,
		protected tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		protected walletService: TradableWalletListService,
		protected userService: UserService,
		private masterdataService: MasterdataService,
		protected tradeOfferService: TradeOfferService,
		protected launchDarklyService: LaunchDarklyService,
		private verificationModalFacade: VerificationModalFacade,
		private stockTestService: StockTestService,
		private router: Router,
		protected dialog: MatDialog,
		protected stockTestHelperService: StockTestHelperService,
		protected confirmModalService: ConfirmModalService,
		private savingService: SavingsService,
		protected leveragedTokenFacade: LeveragedTokensFacade
	) {
		super(
			apicallService,
			fiatwalletService,
			infoService,
			tradableListService,
			fiatService,
			accountService,
			walletService,
			userService,
			tradeOfferService,
			launchDarklyService,
			confirmModalService,
			leveragedTokenFacade
		);
		this.init();
	}

	public onTradableSelect(tradable: Tradable): void {
		const wallets = this.walletService.getWalletsByTradableId(tradable.getId());

		if (wallets.length === 1) {
			// if 1 wallet, set it to null to not show multiple wallets screen
			this.tradableId = null;
			this.onWalletSelect(wallets[0]);
		} else {
			// if multiple wallets, go to wallet selection screen
			this.wallets = wallets;
			this.tradableId = tradable.getId();
		}
	}

	public onWalletSelect(wallet: TradableWallet): void {
		const callback: Function = () => this.selectWallet(wallet);

		if (this.stockTestService.needsTest(wallet.getCurrencyId())) {
			this.stockTestHelperService.startTest(callback);
		} else {
			callback();
		}
	}

	private init(): void {
		this.fiats = this.fiatService.list;
		// Set options
		this.fiatCurrencyOptions = this.fiatService.list
			.filter((fiat: Fiat) => fiat.hasWallet())
			.map((fiat: Fiat) => ({
				name: fiat.getMultiLangField("name", this.userService.user.getLanguage()),
				iconUrl: fiat.getLogo(),
				value: fiat
			}));

		// Set default option
		this.selectedFiat = this.fiatCurrencyOptions.find(
			(item) => (item.value as Fiat).getId() === this.defaultFiat.getId()
		).value;
		this.updateProviders();
	}

	public ngOnInit(): void {
		this.initOfferFromParams();
		this.savingsModel.tradeData = this.data;
		super.ngOnInit();
		if (this.showSavingsIntro) {
			this.showSavingsIntroModal = true;
			this.setEdit(-1);
		}
		this.savingsMigrateToFiatIdEnabled$.pipe(take(1)).subscribe((flag) => {
			this.savingsMigrateToFiatIdEnabled = flag;
		});
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		// this is used to trigger the animation when this modal is called from trade-type-decision-modal
		this.isLoaded = true;
	}

	public getValidationShortcut(): boolean {
		return this.needsValidationShortcut;
	}

	public setValidationShortcut(b: boolean): void {
		this.needsValidationShortcut = b;
		this.pageChangeSubject.next();
	}

	public get sepaAccounts$(): Observable<PaymentAccount[]> {
		return this._sepaAccounts.asObservable();
	}

	public get sepaAccounts(): PaymentAccount[] {
		return this._sepaAccounts.getValue();
	}

	private get defaultFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public userWantsSepaProvider(): boolean {
		return this.wantsSepaProvider;
	}

	public get showInitialBuyInfo(): boolean {
		return !this.masterdataService.isSepaProvider(this.savingsModel.paymentOption);
	}

	public isDeleteSepaAllowed(p: PaymentAccount): boolean {
		return !p.isConfirmed; // Delete only allowed when the mandate hasnt been confirmed yet
	}

	public selectWallet(w: TradableWallet): void {
		const callback: Function = () => super.selectWallet(w);
		if (this.stockTestService.needsTest(w.getCurrencyId())) {
			this.stockTestHelperService.startTest(callback);
		} else {
			callback();
		}
	}

	public returnToProviders(): void {
		this.wantsSepaProvider = false;
		this.clickBack(2);
	}

	public clickBack(num: number): void {
		if (num === 1) {
			this.tradableId = null;
		} else if (num === 2) {
			if (this.needsValidationShortcut) {
				this.needsValidationShortcut = false;
				return;
			}
			if (this.wantsSepaProvider) {
				this.wantsSepaProvider = false;
				this._sepaAccounts.next([]);
				return;
			}
			this.data.stepBack();
		} else if (num === 3) {
			this.data.stepBack();
			this.savingsModel.frequencySelector.primary = "";
			if (!empty(this.sepaAccounts)) {
				this.wantsSepaProvider = false;
				this.needsValidationShortcut = false;
				this.selectedSepaAccount = null;
				this._sepaAccounts.next([]);
			}
		}
	}

	public deleteAccount(p: PaymentAccount): void {
		p.isConfirmed = true;
		this.apicallService.deleteRequest("user/sepa-mandates/" + p.getId()).subscribe(
			() => {
				this.getPaymentAccountList();
				this.infoService.setSuccess(this.translationService.translate("sepa_account_successfully_deleted"));
			},
			(error) => {
				p.isConfirmed = false;
				if (error instanceof ValidationError) {
					this.infoService.setError(error);
				} else {
					console.error(error);
				}
			}
		);
	}

	public getFiatWallets(cashPlusWallets?: ICashPlusFiatWallet[]): FiatWallet[] {
		if (this.selectedFiat) {
			return this.walletsWithFunds(this.minLegacy).filter((w) => w.getCurrencyId() === this.selectedFiat.getId());
		}
		return null;
	}

	public walletsWithFunds(min: number): FiatWallet[] {
		return this.fiatwalletService.list.filter((f) => {
			const fiat = this.fiatService.getItemById(f.getFiatId());
			if (!fiat) {
				return false;
			}
			return toEuroByFiatId(f.getBalance(), this.fiatService.getItemById(f.getFiatId())) >= min;
		});
	}

	public updateProviders(): void {
		this.providerAvailable = [];
		this.providerUnavailable = [];

		this.masterdataService.payment_options
			.filter((p) => !p.is_voucher && p.isEnabled())
			.forEach((option: PaymentOption) => {
				const fid: string = this.selectedFiat.getId();
				const fee: Fee = option.getFeeByFiatId(fid);
				if (!(fee && fee.isSavingsEnabled())) {
					return;
				}

				if (option.hasFiat(this.selectedFiat)) {
					this.providerAvailable.push(option);
				} else {
					this.providerUnavailable.push(option);
				}
			});
	}

	public async selectProvider(p: PaymentOption, createdSepaAccount: boolean = false): Promise<void> {
		if (!p) {
			return;
		}

		if (this.masterdataService.isSepaProvider(p) && !createdSepaAccount) {
			this.getPaymentAccountList();
			return;
		}

		this.savingsModel.tradeData.setMandateUsed(this.masterdataService.isSepaProvider(p));

		if (createdSepaAccount) {
			this.wantsSepaProvider = false;
		}

		let prevProviderId: string = "";
		if (this.savingsModel.paymentOption) {
			prevProviderId = this.savingsModel.paymentOption.getId();
		}

		const providerChange: boolean = !!this.savingsModel.paymentOption && !!p;
		this.savingsModel.paymentOption = p;

		const selectedCurrencyWallet = this.fiatwalletService.list.find(
			(fw) => fw.getFiatId() === this.selectedFiat.getId()
		);
		if (providerChange) {
			this.setEdit(3);
		}
		this.selectFiatWallet(selectedCurrencyWallet);
		this.getApiExample(false, prevProviderId);
		this.pageChangeSubject.next();
	}

	public selectSepaAccount(p: PaymentAccount): void {
		this.selectedSepaAccount = p;
		this.selectProvider(this.getSepaOption(), true);
	}

	private getPaymentAccountList(): void {
		this.apicallService.get("user/sepa-mandates").subscribe(
			(res) => {
				const sepaAccountList: PaymentAccount[] = parseList(res, PaymentAccount);
				this._sepaAccounts.next(sepaAccountList.filter((a) => !a.getIsDeleted()));
				this.sepaAccounts.forEach((account) => {
					const sepaPaymentOption: PaymentOption = this.masterdataService.payment_options.find((p) =>
						this.masterdataService.isSepaProvider(p)
					);
					account.setProviderId(sepaPaymentOption.getId());
					account.setFields(sepaPaymentOption.getFields());
				});

				this.wantsSepaProvider = !empty(this.sepaAccounts);
				if (!this.wantsSepaProvider) {
					this.needsValidationShortcut = true;
				}

				this.pageChangeSubject.next();
				this.cdr.markForCheck();
			},
			(error) => {
				if (error instanceof ValidationError) {
					this.infoService.setError(error);
				}
				console.error(error);
			}
		);
	}

	public showSepaLeadTimeWarning(): boolean {
		return this.isThirdStep && this.masterdataService.isSepaProvider(this.savingsModel.paymentOption);
	}

	public getSelectedCurrencyName(): string {
		return this.getTradable().getName() + " (" + this.getTradable().getSymbol() + ")";
	}

	public onSepaCreated(p: PaymentAccount): void {
		this.selectedSepaAccount = p;
		this.needsValidationShortcut = false;
		this.selectProvider(this.getSepaOption(), true);
	}

	public getNextScheduledBuy(): string {
		if (this.savingsModel.frequencySelector) {
			return this.savingsModel.getNextBuyFormatted();
		}
		return "";
	}

	public onFrequencyModelUpdate($event): void {
		this.savingsModel.frequencySelector = $event;
		this.frequencyStandard = this.savingsModel.frequencySelector.primary;
		this.savingsModel.limitsExceeded = false; // Reset the exceeding flag for any change in the dropdowns
		this.getApiExample();
	}

	// Override - so we don't have prices/default API call anymore - not needed in recurring buy
	protected updateAmounts(): void {
		this.priceSubject$.next(new Price());
		this.data.setUserWants(SELECTED_CURRENCY.FIAT);
		this.doTrapFocusTime();
	}

	// Override - so we don't care about this.price received from prices/default
	public get isThirdStep(): boolean {
		return (this.wallet != null && this.fiatWallet != null && this.isEdit(0)) || this.isEdit(3);
	}

	public goToSummaryPressed(): void {
		if (!this.isSavingsDisabled()) {
			if (this.masterdataService.isSepaProvider(this.savingsModel.paymentOption)) {
				this.savingsModel.tradeData.setMandateUsed(true);
			}
			this.getApiExample(true);
		}
	}

	private validateBeforeSummary(): void {
		if (this.isAmountSmallerThanMin()) {
			// Force to Min
			this.setToMin();
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
		} else if (this.isAmountBiggerThanMax()) {
			// Force to Max
			this.setToMax();
			this.infoService.setError(this.translationService.translate("recurring_amount_larger_than_wallet_balance"));
		} else {
			// Activates summary
			super.finish();
		}

		this.pageChangeSubject.next();
	}

	// Send plan after it (and mandate in case of a new direct debit mandate) is confirmed by user
	public sendPlan(): void {
		if (this.selectedSepaAccount) {
			this.apiCallOption.data["sepa_mandate_id"] = this.selectedSepaAccount.getId();
		}
		this.apicallService.post("savings/plans", this.apiCallOption).subscribe(
			(res) => {
				this.savingsModel.paymentPlan = res[0];
				this.fetchTradableWallet();
				this.showSmsPinVerification = true;
				this.showMandateConfirmation = false;
				this.pageChangeSubject.next();
			},
			(error) => Helper.handleError(error)
		);
	}

	/*
		Fetches the new wallet auto-generated by API. And removes the dummy wallet:
	 */
	public fetchTradableWallet(): void {
		const oldWalletId: string = this.savingsModel.tradeData.getWalletId(); // Dummy wallet id
		const service = this.walletService.serviceForId(oldWalletId);
		const oldWallet = service.getItemById(oldWalletId);
		if (!oldWallet || !oldWallet.isNew()) {
			return;
		}

		const realWalletId: string = this.savingsModel.paymentPlan.getAssetWalletId(); // Wallet id received from API
		this.apicallService.get("wallet/" + realWalletId, new ApicallOption(service.WalletType)).subscribe((res) => {
			const realWallet = res[0];
			service.upsert(realWallet);
			if (oldWallet.getCurrencyId() === realWallet.getCurrencyId()) {
				service.deleteItem(oldWallet);
			}
		});
	}

	// Plan is confirmed, if the mandate is not yet confirmed show the confirmation modal
	public planConfirmed(options: ApicallOption): void {
		this.apiCallOption = options;
		if (!this.selectedSepaAccount || this.selectedSepaAccount.isConfirmed) {
			this.sendPlan();
		} else {
			this.showMandateConfirmation = true;
			this.pageChangeSubject.next();
		}
	}

	public pinConfirmed(plan: SavingsPlan): void {
		// Add the received plan to current model
		this.savingsModel.paymentPlan = plan;
		this.saved = true;
		this.modalCanForceDismiss = true;
		this.fiatwalletService.transactionReceived();
		super.close();

		if (
			this.masterdataService.isSepaProvider(
				this.savingsModel.paymentPlan.getSavingPaymentAccount().getPaymentOption()
			)
		) {
			// Fetch list of stakeables, important for savings success, to check if Vision is stakeable
			if (this.getTradable().getCurrencyType() === TRADABLES_TYPE.CRYPTO) {
				this.stakingFacade.updateFilteredAssets("", "");
			}
			this.modalService.create(SavingsSuccessComponent, { data: plan }, { backdropDismiss: true }).present();
		} else {
			this.modalService
				.create(
					DepositPendingComponent,
					{
						transaction: plan.getSavingsPayments()[0].getFiatTransaction(),
						isSavings: true
					},
					{ backdropDismiss: false }
				)
				.present();
		}
	}

	// Handling ConfirmMandateModal click events
	public mandateConfirmationModalAction(action: string): void {
		if (action === "abort") {
			this.closeAll();
		} else if (action === "back") {
			this.showMandateConfirmation = false;
		} else if (action === "confirm") {
			this.sendPlan();
		}
	}

	public userIsVerified(): boolean {
		return this.userService.user.isVerified();
	}

	public userIsDocumented(): boolean {
		return this.userService.user.isDocumented();
	}

	public isVerificationPending(): boolean {
		return this.userService.isVerificationPending();
	}

	public verify(): void {
		if (!this.verificationModalFacade.openVerification()) {
			this.router.navigate(VERIFICATION_MODAL_ROUTE, { skipLocationChange: true });
		}
	}

	public closeVerify(): void {
		this.trackCancel();
		this.modalCanForceDismiss = true;
		this.modalService.popAll();
	}

	public getBpSelectorModel(): BpSelectorFrequencyModel {
		return this.savingsModel.frequencySelector.primary ? this.savingsModel.frequencySelector : null;
	}

	public receivedFrequencies(): boolean {
		return !empty(this.savingsModel.planExample.getPlanFrequencies());
	}

	public getSepaOption(): PaymentOption {
		return this.masterdataService.payment_options.find((p) => this.masterdataService.isSepaProvider(p));
	}

	private getApiExample(
		summaryOnSuccess: boolean = false,
		previousProviderId: string = this.savingsModel.paymentOption.getId()
	): void {
		const wallet: TradableWallet = this.walletService.getItemById(this.savingsModel.tradeData.getWalletId());

		const tradable = wallet.isDefault()
			? "&asset_id=" + this.savingsModel.tradeData.getCurrencyId()
			: "&wallet_id=" + this.savingsModel.tradeData.getWalletId();
		let examplePath =
			"savings/plan-limits" +
			"?fiat_id=" +
			this.savingsModel.tradeData.getFiatId() +
			"&fiat_wallet_id=" +
			this.savingsModel.tradeData.getFiatWalletId() +
			tradable +
			"&payment_option_id=" +
			this.savingsModel.paymentOption.getId();

		if (this.savingsMigrateToFiatIdEnabled) {
			examplePath =
				"savings/plan-limits" +
				"?fiat_id=" +
				this.savingsModel.tradeData.getFiatId() +
				tradable +
				"&payment_option_id=" +
				this.savingsModel.paymentOption.getId();
		}
		if (
			!empty(this.savingsModel.frequencySelector.primary) &&
			previousProviderId === this.savingsModel.paymentOption.getId()
		) {
			examplePath =
				examplePath +
				"&frequency=" +
				this.savingsModel.frequencySelector.primary +
				"&day=" +
				(this.savingsModel.frequencySelector.secondary ? this.savingsModel.frequencySelector.secondary : "0") +
				"&time=" +
				this.savingsModel.frequencySelector.time +
				"&time_zone=" +
				encodeURIComponent(this.savingsModel.getTimeZone());
		}
		examplePath = examplePath + (+this.amount ? "&fiat_amount=" + +this.amount : "");

		this.apicallService.get(examplePath, new ApicallOption(SavingsPlanExample)).subscribe(
			(data) => {
				this.savingsModel.planExample = data[0];
				const expiryDate = new Date(Date.now() + ONE_HOUR_EXPIRY);
				Helper.setCookie(
					SAVINGS_VISION_BENEFIT_FEE_COOKIE + this.savingsModel.tradeData.getCurrencyId(),
					data[0]?.vsn_fee_token_collection?.fee_discount,
					false,
					false,
					true,
					true,
					expiryDate
				);
				Helper.setCookie(
					SAVINGS_ELIGIBLE_VISION_BENEFIT_FEE_COOKIE + this.savingsModel.tradeData.getCurrencyId(),
					data[0]?.vsn_fee_token_collection?.eligibleForVsn,
					false,
					false,
					true,
					true,
					expiryDate
				);
				if (empty(this.frequencyStandard)) {
					this.frequencyStandard = this.savingsModel.planExample
						.getPlanFrequencies()
						.find((f) => f.is_default).label;
				}
				this.checkAmountBiggerThanMax();
				if (summaryOnSuccess) {
					this.validateBeforeSummary();
				}
			},
			(error) => {
				if (error instanceof ErrorClass) {
					this.infoService.setError(error);

					const callback: Function = () => this.selectWallet(wallet);

					// trigger appropriateness test if code return match appr_test is required
					if (error.containsCode(APPROPRIATENESS_REQUIRED_ERROR_CODE)) {
						this.stockTestHelperService.startTest(callback, true);
					}

					if (error instanceof ValidationError) {
						if (error.metaContains("fiat_amount")) {
							const metaType = error.responseErrors[0].meta["type"]
								? error.responseErrors[0].meta["type"]
								: "";
							const metaAmount = error.responseErrors[0].meta["amount"]
								? error.responseErrors[0].meta["amount"]
								: "";
							if (metaAmount && this.savingsModel.planExample) {
								if (metaType === "min") {
									this.savingsModel.planExample.setMin(+metaAmount);
								}
								if (metaType === "max") {
									this.savingsModel.planExample.setMax(+metaAmount);
								}
								this.amount = metaAmount;
							}
						}
						if (error.containsCode("savings.limit.exceeded") && this.savingsModel.planExample) {
							this.amount = "0";
							this.savingsModel.planExample.setMax(0);
							this.savingsModel.planExample.setDefaults([]);
							this.savingsModel.limitsExceeded = true;
						}
					}
				} else {
					console.error(error);
				}
			}
		);
	}

	// Override
	public get minLegacy(): number {
		if (this.savingsModel.planExample) {
			return this.savingsModel.planExample.getMin();
		}
		return 0;
	}

	public getMaxAmount(): number {
		if (this.savingsModel.planExample) {
			return this.savingsModel.planExample.getMax();
		}
		return 0;
	}

	public get aboveMaxLock(): boolean {
		if (this.savingsModel.planExample.getMax()) {
			return +this.amount > this.savingsModel.planExample.getMax();
		}
		return false;
	}

	public isSavingsDisabled(): boolean {
		return this.getAmountFiat() === 0 || this.getAmountFiat() < this.minLegacy || this.aboveMaxLock;
	}

	// Override
	public setToMax(): void {
		this.amount = "" + this.savingsModel.planExample.getMax();
		this.trackSelect("amount");
	}

	public getFiatAmounts(): string[] {
		if (!this.savingsModel.planExample) {
			return [];
		}
		return this.savingsModel.planExample
			.getDefaults()
			.slice(0, 2)
			.filter((item) => +item <= this.getMaxAmount())
			.map((item) => item + "");
	}

	// Override
	public setFiatAmount(s: string): void {
		this.amount = s;
	}

	private checkAmountBiggerThanMax(): void {
		if (this.isAmountBiggerThanMax()) {
			this.setToMax();
		}
	}

	private isAmountBiggerThanMax(): boolean {
		return this.savingsModel.planExample && +this.amount > this.savingsModel.planExample.getMax();
	}

	private isAmountSmallerThanMin(): boolean {
		return this.savingsModel.planExample && +this.amount < this.savingsModel.planExample.getMin();
	}

	public setToMin(): void {
		this.amount = "" + this.savingsModel.planExample.getMin();
	}

	public getFrequencyString(): string {
		if (!empty(this.frequencyStandard)) {
			return this.frequencyStandard;
		}
		return "";
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		this.trackCancel();
		return true;
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (
			(this.showSavingsIntroModal && this.isEdit(0)) ||
			(this.isFirstStep && this.tradableId) ||
			(this.isSecondStep && this.isEdit(0)) ||
			(this.isThirdStep && !this.isEdit(3))
		) {
			header.left = "back";
		} else if (
			(this.isFirstStep && this.isEdit(1)) ||
			(this.isSecondStep && this.isEdit(2)) ||
			(this.isThirdStep && this.isEdit(3))
		) {
			header.left = "abort";
		}
		if (
			(this.isFirstStep && this.isEdit(1)) ||
			(this.isSecondStep && this.isEdit(2)) ||
			(this.isThirdStep && this.isEdit(3))
		) {
			header.right = "ok";
		} else if (
			(this.isIntroductionStep && this.isEdit(-1)) ||
			(this.isFirstStep && !this.isEdit(1)) ||
			(this.isSecondStep && !this.isEdit(2)) ||
			this.isThirdStep
		) {
			header.right = "abort";
		}

		return header;
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			if (this.isFirstStep) {
				this.resetTradable();
				if (this.showSavingsIntroModal) {
					this.setEdit(-1);
				}
			} else {
				//in summary when user returns back to amount selection we need to reset the plan example
				if (this.isThirdStep) {
					this.savingsModel.planExample = new SavingsPlanExample();
				}

				if (this.isSecondStep) {
					this.tradableId = null;
				}
				this.clickBack(this.stepNumber);
			}
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		const tradable: Tradable = this.getTradable();
		const symbol = tradable.isToken() ? tradable.getName() : tradable.getSymbol(false, true);

		if (this.isIntroductionStep) {
			return this.translate("recurring_buy_modal_title_buy_cryptocoins");
		}

		switch (this.stepNumber) {
			case 1:
				return this.translate("recurring_buy_modal_title_buy_cryptocoins");
			case 2:
				return this.translate("recurring_buy_modal_title_buy_cryptocoin", [symbol]);
			case 3:
				return !tradable.isToken()
					? this.translate("recurring_buy_modal_title_buy_cryptocoin", [symbol])
					: this.translate("recurring_buy_modal_title_buy_cryptocoins");
			default:
				return "";
		}
	}

	public initTracking(): void {
		this.pageChangeSubject.subscribe(() => {
			if (this.showMandateConfirmation || this.showSmsPinVerification || this.summary) {
				const name =
					"Savings " +
					(this.showSmsPinVerification
						? "Enter PIN"
						: this.showMandateConfirmation
							? "Confirm SEPA Mandate"
							: "Amount Summary");
				const path =
					"/trade/savings/" +
					(this.showSmsPinVerification
						? "pin"
						: this.showMandateConfirmation
							? "mandateconfirmation"
							: "summary");
				this.rudderstackPage(name, path, {
					asset_category: this.getTradable().getSymbol(true),
					fiat_currency: this.getFiat().getSymbol(true),
					savings_method: this.savingsModel.paymentOption.getShortName(),
					amount: this.amount,
					bfc_enabled: this.data.bestFeeOptIn
				});
			} else if (this.isThirdStep) {
				this.rudderstackPage("Savings Amount Selection", "/trade/savings/amount", {
					asset_category: this.getTradable().getSymbol(true),
					fiat_currency: this.getFiat().getSymbol(true),
					savings_method: this.savingsModel.paymentOption.getShortName()
				});
			} else if (this.isSecondStep) {
				const symbol = this.getTradable().getSymbol(true);
				if (!this.getValidationShortcut()) {
					if (this.userWantsSepaProvider()) {
						this.rudderstackPage("Savings SEPA Mandate Selection", "/trade/savings/mandateselection", {
							asset_category: symbol,
							fiat_currency: this.getFiat().getSymbol(true),
							savings_method: "sepa"
						});
					} else {
						this.rudderstackPage("Savings Payment Method Selection", "/trade/savings", {
							asset_category: symbol
						});
					}
				} else {
					this.rudderstackPage("Savings Add SEPA Mandate", "/trade/savings/addmandate", {
						asset_category: symbol,
						fiat_currency: this.getFiat().getSymbol(true),
						savings_method: "sepa"
					});
				}
			} else if (this.isFirstStep) {
				this.rudderstackPage("Savings Asset Selection", "/trade/savings");
			}
		});
	}

	public getItbData(): ITBTrade {
		let amount = "";
		if (+this.data?.getAmountFiat() >= +this.savingsModel?.planExample?.getMin()) {
			amount = "" + +this.data?.getAmountFiat() * this.selectedFiat.toEuroRate();
		}
		if (+this.data?.getAmountFiat() > +this.savingsModel?.planExample?.getMax()) {
			amount = "" + +this.savingsModel?.planExample?.getMax() * this.selectedFiat.toEuroRate();
		}
		return {
			amount: amount,
			itbRate: this.savingsModel?.planExample?.getITBRate()
		};
	}

	public goToFirstStep(): void {
		this.setEdit(0);
	}

	private initOfferFromParams(): void {
		if (this.data instanceof Offer) {
			return;
		}

		const offer = new Offer();
		offer.setType(TRADE_TYPE.RECURRING_BUY);

		if (this.walletId) {
			const wallet = this.walletService.getItemById(this.walletId);
			if (wallet) {
				offer.setWalletId(wallet.getId());
				offer.setCurrencyId(wallet.getCurrencyId());
			}
		}

		this.data = offer;
	}
}
